import {ENTERPRISE_PLAN_NAME, FREE_PLAN_NAME} from '../../../constants/plan';

export const featuresTextMapping: Record<
  string,
  {displayName: string; description?: string; showIcon?: boolean}
> = {
  users_limit: {
    displayName: 'Users',
  },
  events_limit: {
    displayName: 'Events',
  },
  diagrams_per_event_limit: {
    displayName: 'Diagrams per event',
    description: 'Diagram deletion is limited to Premium',
  },
  attendees_limit: {
    displayName: 'Attendees per attendee list',
  },
  professionally_drawn_floor_plans_enabled: {
    displayName: 'Professionally drawn floor plans',
    showIcon: true,
  },
  property_unique_sqft_limit: {
    displayName: 'Professionally drawn floor plans',
    showIcon: true,
  },
  collaborators_limit: {
    displayName: 'Unlimited collaborators',
  },
  core_diagramming_enabled: {
    displayName: 'Core diagramming features',
    description:
      'Create diagrams, add attendees, and collaborate with event partners.',
    showIcon: true,
  },
  diagram_efficiency_features_enabled: {
    displayName: 'Advanced diagramming features',
    description:
      'Diagram more efficiently with automation. Work faster by saving layouts, templates, and favorites.',
    showIcon: true,
  },
  attendee_management_enabled: {
    displayName: 'Additional attendee management features',
    description:
      'Customize your attendee data collection. Check in attendees from any device.',
    showIcon: true,
  },
  diagram_export_without_cvent_logo: {
    displayName: 'Diagram export without Cvent logo',
  },
  venue_library_enabled: {
    displayName: 'Venue library',
  },
  diagram_status_enabled: {
    displayName: 'Diagram status',
  },
  diagram_favorites_enabled: {
    displayName: 'Diagram favorites',
  },
  diagram_templates_enabled: {
    displayName: 'Diagram templates',
  },
  diagram_layouts_enabled: {
    displayName: 'Diagram layouts',
  },
  diagram_table_numbering_enabled: {
    displayName: 'Diagram table numbering',
  },
  beo_upload_enabled: {
    displayName: 'BEO upload',
  },
  attendee_custom_fields_enabled: {
    displayName: 'Attendee custom fields',
  },
  check_in_enabled: {
    displayName: 'Check-in',
  },
  venue_management_enabled: {
    displayName: 'Venue management',
  },
  property_added_venue_library_enabled: {
    displayName: 'Property added venue library',
  },
  manage_venue_details_enabled: {
    displayName: 'Manage venue details',
  },
  floor_plan_usage_analytics_enabled: {
    displayName: 'Floor plan usage analytics',
  },
  create_photorealistic_3d_enabled: {
    displayName: 'Create photorealistic 3D',
  },
  event_reg_integration_enabled: {
    displayName: 'Event registration integration',
  },
  ifp_enabled: {
    displayName: 'IFP',
  },
  public_links_enabled: {
    displayName: 'Public links',
  },
  view_3d_enabled: {
    displayName: 'View 3D',
  },
  diagram_assistant_enabled: {
    displayName: 'Diagram assistant',
  },
};

// Add feature keys here to display them in the comparison table
export const getFeaturesForClientType = (clientType?: string): string[] => {
  const baseFeatures = [
    'users_limit',
    'events_limit',
    'diagrams_per_event_limit',
    'attendees_limit',
    'collaborators_limit',
    'core_diagramming_enabled',
    'diagram_efficiency_features_enabled',
    'attendee_management_enabled',
  ];

  const floorPlanFeature =
    clientType === 'supplier'
      ? 'property_unique_sqft_limit'
      : 'professionally_drawn_floor_plans_enabled';

  return [
    ...baseFeatures.slice(0, 4),
    floorPlanFeature,
    ...baseFeatures.slice(4),
  ];
};

// Custom renderer for specific features
export const getCustomRenderer = (featureKey: string) => {
  if (featureKey === 'users_limit') {
    return (value: string | number | boolean | null, planType?: string) => {
      if (planType?.includes(ENTERPRISE_PLAN_NAME) && value === null) {
        return 'Custom';
      }
      return value;
    };
  }
  if (featureKey === 'professionally_drawn_floor_plans_enabled') {
    return (value: string | number | boolean | null) => {
      if (value === true) {
        return 'Unlimited';
      }
      return false;
    };
  }
  if (featureKey === 'events_limit') {
    return (value: string | number | boolean | null, planType?: string) => {
      if (value === null) {
        return 'Unlimited';
      }
      if (typeof value === 'number') {
        // Check if this is a free plan
        if (planType?.includes(FREE_PLAN_NAME)) {
          return `${value} (fixed)`;
        }
        return `${value} events/year`;
      }
      return value;
    };
  }
  if (featureKey === 'property_unique_sqft_limit') {
    return (value: string | number | boolean | null, planType?: string) => {
      if (planType?.includes(ENTERPRISE_PLAN_NAME) && value === null) {
        return 'Custom';
      }

      if (typeof value === 'number') {
        return `${value.toLocaleString()} sq. ft.`;
      }

      if (typeof value === 'string') {
        const numericValue = Number(value);
        if (!Number.isNaN(numericValue)) {
          return `${numericValue.toLocaleString()} sq. ft.`;
        }
      }

      return false;
    };
  }
  if (featureKey === 'collaborators_limit') {
    return (value: string | number | boolean | null) => {
      // Render null (unlimited) as true (check mark icon)
      if (value === null) {
        return true;
      }
      return value;
    };
  }
  return null;
};
